/**
 * The pre-submit half of the content lint (doc 05-admin/10 §2.1; doc 03 §6.7). It runs the
 * server's own rules the way `app/content_lint/engine.check()` does for plain copy: inactive
 * rules, name- and badge-scoped rules, and Hindi rules on English text are skipped, and `’` is
 * folded to `'` one character for one so a span in the folded text is the same span in the
 * original. A `{temple_name}` rule needs the published temples, which only the server has, and
 * a pattern JavaScript cannot compile is left to the server too — a miss here is a refusal on
 * save, never a pass.
 */
import type { LintRule } from './api/hooks/contentLint';

export interface LintHit {
  ruleId: string;
  ruleClass: string;
  severity: string;
  matched: string;
  start: number;
  end: number;
}

const TEMPLE_TOKEN = '{temple_name}';

function applies(rule: LintRule, language: string): boolean {
  const scope = rule.scope ?? {};
  return (
    rule.is_active &&
    !rule.pattern.includes(TEMPLE_TOKEN) &&
    !scope.names_only &&
    !scope.badges_only &&
    (rule.language !== 'hi' || language === 'hi')
  );
}

function compile(pattern: string): RegExp | null {
  try {
    return new RegExp(pattern, 'giu');
  } catch {
    return null;
  }
}

/** Every match, in text order. */
export function lintText(
  text: string,
  rules: readonly LintRule[],
  language = 'en',
): LintHit[] {
  if (!text) return [];
  const folded = text.replace(/’/g, "'");
  const hits: LintHit[] = [];
  for (const rule of rules) {
    if (!applies(rule, language)) continue;
    const re = compile(rule.pattern);
    if (!re) continue;
    for (const m of folded.matchAll(re)) {
      const start = m.index;
      const end = start + m[0].length;
      if (end === start) continue;
      hits.push({
        ruleId: rule.rule_id,
        ruleClass: rule.rule_class,
        severity: rule.severity,
        matched: text.slice(start, end),
        start,
        end,
      });
    }
  }
  return hits.sort((a, b) => a.start - b.start || a.end - b.end);
}

/** The text cut into plain runs and flagged runs; overlapping hits merge into one mark. */
export function segments(
  text: string,
  hits: readonly LintHit[],
): { text: string; flagged: boolean }[] {
  const out: { text: string; flagged: boolean }[] = [];
  let at = 0;
  for (const h of hits) {
    if (h.end <= at) continue;
    const start = Math.max(h.start, at);
    if (start > at) out.push({ text: text.slice(at, start), flagged: false });
    out.push({ text: text.slice(start, h.end), flagged: true });
    at = h.end;
  }
  if (at < text.length) out.push({ text: text.slice(at), flagged: false });
  return out;
}
